import { useLocalize } from "@/lib/i18n/use-localize";
/**
 * QL-281 — Add / edit goal form for the Goal List modal.
 *
 * One explicit form for both modes: goal type, name, optional description and
 * — when editing a goal on a collection with platforms — the platform. Nothing
 * is persisted while the form is open: Save creates the goal (add) or writes
 * the changed fields plus the platform pick (edit); Cancel discards.
 *
 * The fields are the same components the goal page's details widget renders
 * (`GoalTypePicker`, `GoalPlatformField`, `GoalDescriptionField`), so the two
 * surfaces read alike; only the persistence differs (autosave vs Save).
 *
 * Save order on edit: the goal PATCH runs first, then the platform pick. The
 * platform list is always evaluated by the server for the type currently
 * selected in the form (`?goalType=`), so a pick is never stale against the
 * type being saved: after the PATCH stores that type, the pick is valid.
 */

import type { GoalTypeOption } from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, useTransition } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { goalSnapshotQueryKeys } from "@/lib/hooks/use-flow-instances/use-goal-snapshots";
import {
  goalPlatformQueryKeys,
  useGoalPlatformState,
  useSetGoalPlatform,
} from "@/lib/hooks/use-goal-platforms";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { GoalDescriptionField } from "@/pages/flow-instances/goals/goal-description-field";
import { GoalPlatformField } from "@/pages/flow-instances/goals/goal-platform-field";
import { GoalTypePicker } from "@/pages/flow-instances/goals/goal-type-picker";
import { useGoalPlatformCascade } from "@/pages/flow-instances/goals/use-goal-platform-cascade";
import { T } from "./goal-list-form.i18n";
import type {
  GoalListCreateData,
  GoalListGoal,
  GoalListMutationResult,
  GoalListUpdateData,
  GoalListUpdateResult,
} from "./types";

export type GoalListFormProps = {
  /** The goal being edited; null = add mode. */
  goal: GoalListGoal | null;
  flowInstanceId: string;
  goalTypes: GoalTypeOption[];
  showDescription: boolean;
  onClose: () => void;
  onCreate: (data: GoalListCreateData) => Promise<GoalListMutationResult>;
  onUpdate: (goalId: string, data: GoalListUpdateData) => Promise<GoalListUpdateResult>;
};

export function GoalListForm({
  goal,
  flowInstanceId,
  goalTypes,
  showDescription,
  onClose,
  onCreate,
  onUpdate,
}: GoalListFormProps) {
  const t = useT(T);
  const isEdit = goal !== null;
  const { userLanguage } = useUserLanguage();
  const localize = useLocalize();
  const queryClient = useQueryClient();
  const [name, setName] = useState(goal?.name ?? "");
  const [goalType, setGoalType] = useState(goal?.goalType ?? "");
  const [description, setDescription] = useState(goal?.description ?? "");
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  // QL-36 — a goal may only take a type this client type may pick, plus the
  // type it already holds (kept selectable so an edit never wipes it).
  const selectableTypes = goalTypes.filter(
    (gt) => gt.available !== false || gt.id === goal?.goalType,
  );
  const hasGoalTypes = selectableTypes.length > 0;
  const typeChanged = isEdit && goalType !== (goal.goalType ?? "");

  // ── QP-195 — platform (edit mode only; eligibility is server-owned) ──
  // Evaluated for the type selected in the form, so changing the type refreshes
  // the list to what that type allows before anything is saved.
  // `platformLoading` holds Save while the evaluation for a newly selected
  // type is in flight — otherwise a quick Save after a type change would skip
  // the platform check and write the type alone.
  const { platformState, isLoading: platformLoading } = useGoalPlatformState(
    goal?.id ?? null,
    typeChanged && goalType ? goalType : undefined,
  );
  const setPlatform = useSetGoalPlatform(goal?.id ?? "");
  const handlePlatformCascade = useGoalPlatformCascade({
    goalId: goal?.id ?? "",
    flowInstanceId,
  });
  const [platformId, setPlatformId] = useState<string>("");
  const storedPlatformId = platformState?.selectedId ?? "";
  // Keep whatever is picked while it is eligible for the selected type — the
  // user's own pick included, across type changes (mirrors the server's
  // "never silently switch" sync rule). Only when the current value is not
  // eligible fall back to the stored platform if that still is, else the single
  // eligible platform (the goal page's widget auto-applies that case), else
  // nothing.
  useEffect(() => {
    if (!platformState) return;
    const eligibleIds = platformState.eligible.map((p) => p.id);
    setPlatformId((current) => {
      if (current && eligibleIds.includes(current)) return current;
      const stored = platformState.selectedId ?? "";
      if (eligibleIds.includes(stored)) return stored;
      return eligibleIds.length === 1 ? eligibleIds[0]! : "";
    });
  }, [platformState]);
  const platformApplies = isEdit && !!platformState?.hasPlatforms;
  const platformRequired =
    platformApplies && platformState!.resolved && platformState!.eligible.length > 0;

  const handleOpenChange = (next: boolean) => {
    if (!next) onClose();
  };

  // QP-187 — selecting a type pre-fills the name with the type's label, as the
  // goal details widget does.
  const handleSelectType = (goalTypeId: string) => {
    setGoalType(goalTypeId);
    const selected = selectableTypes.find((gt) => gt.id === goalTypeId);
    if (selected) setName(localize(selected.labelI18n));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (isEdit && platformLoading) return;
    if (hasGoalTypes && !goalType) {
      setError(t.typeRequired);
      return;
    }
    if (!name.trim()) {
      setError(t.nameRequired);
      return;
    }
    // Validate the pick against the eligibility loaded for the selected type
    // at submit time — not just "non-empty" — so a value the reseed effect has
    // not caught up with yet can never reach the platform endpoint.
    const eligibleIds = platformState?.eligible.map((p) => p.id) ?? [];
    const pickIsEligible = !!platformId && eligibleIds.includes(platformId);
    if (platformRequired && !pickIsEligible) {
      setError(t.platformRequired);
      return;
    }
    startTransition(async () => {
      if (!isEdit) {
        const result = await onCreate({
          name: name.trim(),
          ...(goalType ? { goalType } : {}),
          ...(showDescription ? { description: description.trim() || null } : {}),
        });
        if (!result.success) {
          setError(result.error || t.failedToCreateGoal);
          return;
        }
        onClose();
        return;
      }

      // Edit — send only what changed; the server owns the platform cascade a
      // type change triggers.
      const changes: GoalListUpdateData = {};
      if (name.trim() !== goal.name) changes.name = name.trim();
      if (typeChanged && goalType) changes.goalType = goalType;
      if (showDescription && (description.trim() || null) !== (goal.description ?? null)) {
        changes.description = description.trim() || null;
      }
      if (Object.keys(changes).length > 0) {
        const result = await onUpdate(goal.id, changes);
        if (!result.success) {
          setError(result.error || t.failedToUpdateGoal);
          return;
        }
        if (result.platformSync?.changed) handlePlatformCascade(result.platformSync);
        else queryClient.invalidateQueries({ queryKey: goalPlatformQueryKeys.state(goal.id) });
        // QL-375 — a goal-type change re-resolves the goal's mandate server-side.
        if (typeChanged) {
          queryClient.invalidateQueries({ queryKey: goalSnapshotQueryKeys.mandate(goal.id) });
        }
      }

      // Platform pick — a separate endpoint. The pick was evaluated for the type
      // just stored, so it is valid now; skip it only when it already matches.
      if (platformApplies && pickIsEligible && platformId !== storedPlatformId) {
        try {
          const result = await setPlatform.mutateAsync({ platformId, lang: userLanguage });
          if (!result.success) {
            setError(result.error || t.failedToSetPlatform);
            return;
          }
          handlePlatformCascade(result);
        } catch (err) {
          setError(err instanceof Error && err.message ? err.message : t.failedToSetPlatform);
          return;
        }
      }
      onClose();
    });
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? t.editGoalTitle : t.addGoalTitle}</DialogTitle>
          <DialogDescription>
            {isEdit ? t.editGoalDescription : t.addGoalDescription}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6 py-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <GoalTypePicker
              idPrefix="goal-list-form"
              goalTypes={selectableTypes}
              value={goalType}
              onValueChange={handleSelectType}
              disabled={isPending}
            />

            {isEdit && (
              <GoalPlatformField
                id="goal-list-form-platform"
                data-testid="goal-list-platform-section"
                platformState={platformState}
                value={platformId}
                onValueChange={setPlatformId}
                disabled={isPending}
              />
            )}

            <div className="space-y-2">
              <Label htmlFor="goal-list-form-name">
                {t.goalName} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="goal-list-form-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t.goalNamePlaceholder}
                disabled={isPending}
                required
              />
            </div>

            {showDescription && (
              <GoalDescriptionField
                id="goal-list-form-description"
                value={description}
                onChange={setDescription}
                disabled={isPending}
              />
            )}
          </div>

          <DialogFooter>
            <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
            <Button type="submit" disabled={isEdit && platformLoading} loading={isPending}>
              {isEdit ? t.save : t.add}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
